import _ from "lodash";
import {ERROR_CODE, SENDER, STATUS} from "../../components/ai-assistant-chatbot/constants";
import {PACKAGE_LOADED, PACKAGE_UNLOADED} from "../miscellaneous/miscellaneousActions";
import {
  AI_ASSISTANT_AI_FEATURES_FETCH,
  AI_ASSISTANT_AI_SETTINGS_AUTHORIZATION_REQUEST,
  AI_ASSISTANT_AI_SETTINGS_CHANGE_PASSWORD_REQUEST,
  AI_ASSISTANT_AI_SETTINGS_FETCH,
  AI_ASSISTANT_AI_SETTINGS_MODAL_HIDE,
  AI_ASSISTANT_AI_SETTINGS_MODAL_SHOW,
  AI_ASSISTANT_AI_SETTINGS_SUBMIT,
  AI_ASSISTANT_ANALYSIS_FETCH,
  AI_ASSISTANT_CHATBOT_CHAT_ENTRY_ADD,
  AI_ASSISTANT_CHATBOT_CHAT_RESET,
  AI_ASSISTANT_CHATBOT_DIALOG_VISIBILITY_SET,
  AI_ASSISTANT_CHATBOT_LAST_QUERY_SET,
  AI_ASSISTANT_CHATBOT_MESSAGE_UPDATE,
  AI_ASSISTANT_CHATBOT_PACKAGE_ID_SET,
  AI_ASSISTANT_CHATBOT_REQUEST_SEND,
  AI_ASSISTANT_CHATBOT_REQUEST_STOP,
  AI_ASSISTANT_CHATBOT_USER_MESSAGE_CHANGE,
  AI_ASSISTANT_METADATA_FETCH,
  AI_ASSISTANT_NODES_FETCH,
  AI_ASSISTANT_SEARCH_ENGINE_LIST_FETCH,
  AI_ASSISTANT_SEARCH_ENGINE_LIST_MODAL_HIDE,
  AI_ASSISTANT_SEARCH_ENGINE_LIST_MODAL_SHOW,
  AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_DELETE,
  AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_FETCH,
  AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_MODAL_HIDE,
  AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_MODAL_SHOW,
  AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_SUBMIT,
  AI_ASSISTANT_STATE_RESET,
  AI_ASSISTANT_VIEW_ERROR_HIDE,
  AI_ASSISTANT_VIEW_SUBMIT
} from "./aiAssistantAction";
import {REQUEST_ERROR, REQUEST_START, REQUEST_SUCCESS} from "../../middlewares/request/requestActions";
import {getResourceFromCatalogById} from "../../utils/catalog";
import {normalizeQueryParams} from "../../utils/odata";

const INITIAL_AI_SETTINGS = {
  search: {
    enabled: false,
    engineId: 0
  },
  oDataSearch: {
    enabled: false,
    engineId: 0
  },
  metadata: {
    enabled: false,
    engineId: 0
  },
  analyze: {
    enabled: false,
    engineId: 0
  }
};

const initialChatState = {
  visible: false,
  sessionId: null,
  message: "",
  history: [],
  suggestions: [],
  isFullscreen: false,
  lastQuery: null
};

const initialViewSaveState = {
  isViewErrorVisible: false,
  viewErrorMessage: null
};

const initialNodeAiSettingsState = {
  authToken: null,
  nodes: null,
  nodeId: null,
  aiSettings: null,
  isNodeAISettingsModalVisible: false,
  isSearchEngineListVisible: false,
  isAiSearchEngineSettingsVisible: false,
  searchEngines: null,
  searchEngineId: null,
  searchEngineSettings: null
};

const initialState = {
  // context
  packageId: null,
  isAutoPackageScoped: false,
  // chatbot
  ...initialChatState,
  // metadata
  metadata: null,
  // view save
  ...initialViewSaveState,
  // ai node settings
  aiNodeSettings: initialNodeAiSettingsState,
  enabledFeatures: null
};

const aiAssistantReducer = (state = initialState, action) => {
  switch (action.type) {
    case AI_ASSISTANT_STATE_RESET:
      return initialState;

    case PACKAGE_LOADED: {
      return {
        ...state,
        ...initialChatState,
        metadata: null,
        packageId: action.payload.packageId,
        isAutoPackageScoped: false
      };
    }
    case PACKAGE_UNLOADED: {
      return {
        ...state,
        ...initialChatState,
        metadata: null,
        packageId: null
      };
    }

    case AI_ASSISTANT_CHATBOT_DIALOG_VISIBILITY_SET:
      return {
        ...state,
        visible: action.payload.visibility
      };
    case AI_ASSISTANT_CHATBOT_USER_MESSAGE_CHANGE: {
      const message = action.payload.append
        ? state.message.endsWith(" ")
          ? state.message + action.payload.message
          : state.message + " " + action.payload.message
        : action.payload.message;

      return {
        ...state,
        message: message
      };
    }
    case AI_ASSISTANT_CHATBOT_CHAT_RESET:
      return {
        ...state,
        packageId: state.isAutoPackageScoped ? null : state.packageId,
        ...initialChatState,
        visible: true
      };
    case AI_ASSISTANT_CHATBOT_CHAT_ENTRY_ADD:
      return {
        ...state,
        history: [...state.history, action.payload.entry]
      };
    case AI_ASSISTANT_CHATBOT_MESSAGE_UPDATE: {
      const {messageIdx, updates} = action.payload;

      const newHistory = _.cloneDeep(state.history);
      newHistory[messageIdx] = _.merge(newHistory[messageIdx], updates);

      return {
        ...state,
        history: newHistory
      };
    }
    case AI_ASSISTANT_CHATBOT_REQUEST_STOP: {
      const history = _.cloneDeep(state.history).filter(message => message.status !== STATUS.pending);
      return {
        ...state,
        history: history
      };
    }
    case AI_ASSISTANT_CHATBOT_PACKAGE_ID_SET: {
      return {
        ...state,
        packageId: action.payload.packageId,
        isAutoPackageScoped: true
      };
    }
    case AI_ASSISTANT_CHATBOT_LAST_QUERY_SET: {
      return {
        ...state,
        lastQuery: action.payload.lastQuery
      };
    }

    case AI_ASSISTANT_VIEW_ERROR_HIDE: {
      return {
        ...state,
        isViewErrorVisible: false
      };
    }

    case AI_ASSISTANT_AI_SETTINGS_MODAL_SHOW:
      return {
        ...state,
        aiNodeSettings: {
          ...state.aiNodeSettings,
          isNodeAISettingsModalVisible: true,
          nodeId: action.payload.nodeId
        }
      };
    case AI_ASSISTANT_AI_SETTINGS_MODAL_HIDE:
      return {
        ...state,
        aiNodeSettings: {
          ...initialNodeAiSettingsState,
          authToken: state.aiNodeSettings.authToken
        }
      };
    case AI_ASSISTANT_SEARCH_ENGINE_LIST_MODAL_SHOW:
      return {
        ...state,
        aiNodeSettings: {
          ...state.aiNodeSettings,
          isSearchEngineListVisible: true
        }
      };
    case AI_ASSISTANT_SEARCH_ENGINE_LIST_MODAL_HIDE:
      return {
        ...state,
        aiNodeSettings: {
          ...state.aiNodeSettings,
          isSearchEngineListVisible: false,
          searchEngineId: null,
          searchEngineSettings: null
        }
      };
    case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_MODAL_SHOW:
      return {
        ...state,
        aiNodeSettings: {
          ...state.aiNodeSettings,
          isAiSearchEngineSettingsVisible: true,
          searchEngineId: action.payload.searchEngineId
        }
      };
    case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_MODAL_HIDE:
      return {
        ...state,
        aiNodeSettings: {
          ...state.aiNodeSettings,
          isAiSearchEngineSettingsVisible: false,
          searchEngineId: null,
          searchEngineSettings: null
        }
      };

    case REQUEST_START: {
      switch (action.payload.label) {
        case AI_ASSISTANT_CHATBOT_REQUEST_SEND: {
          const {isFollowUpMessage = false, followUpPendingMessage = null} = action.payload.extra;

          const newHistory = [...state.history];

          if (!isFollowUpMessage) {
            newHistory.push({
              message: action.payload.extra.message,
              sender: SENDER.user,
              status: STATUS.success
            });
          }

          newHistory.push({
            message: followUpPendingMessage || null,
            sender: SENDER.bot,
            status: STATUS.pending
          });

          return {
            ...state,
            message: "",
            history: newHistory
          };
        }
        default:
          return state;
      }
    }

    case REQUEST_SUCCESS: {
      switch (action.payload.label) {
        case AI_ASSISTANT_CHATBOT_REQUEST_SEND: {
          const {catalog, message, isFollowUpMessage = false} = action.payload.extra;
          const {sessionId, suggestions, response, queryInfo, data, count, results, onLoadEnd, onDataFetch, metadata} =
            action.payload.response;

          const resource = data ? getResourceFromCatalogById(catalog, data.resourceIdentifier) : null;

          const newHistory = state.history.filter(({status}) => status !== STATUS.pending);
          newHistory.push({
            message: response,
            sender: SENDER.bot,
            status: STATUS.loading,
            queryInfo: queryInfo,
            data: resource
              ? {
                  ...resource,
                  ...data,
                  packageId: data.packageIdentifier,
                  resourceId: data.resourceIdentifier,
                  userMessage: message,
                  query: normalizeQueryParams(data.query),
                  metadata: metadata,
                  config: {
                    ...data.layout,
                    categoryDim: data.layout?.category,
                    showTitle: true,
                    disableFilters: true,
                    disableSorting: true,
                    disableWheelZoom: true
                  },
                  viewer: data.layout.viewer,
                  layout: undefined
                }
              : null,
            count: count,
            results: results,
            isFollowUpMessage: isFollowUpMessage,
            onLoadEnd: onLoadEnd,
            onDataFetch: onDataFetch
          });

          if (data && !resource) {
            newHistory.push({
              message: null,
              sender: SENDER.bot,
              status: STATUS.error,
              errorCode: ERROR_CODE.generic
            });
          }

          return {
            ...state,
            sessionId: sessionId,
            history: newHistory,
            suggestions: suggestions,
            isFullscreen: state.isFullscreen || !!data
          };
        }
        case AI_ASSISTANT_METADATA_FETCH: {
          return {
            ...state,
            metadata: action.payload.response?.response || null
          };
        }
        case AI_ASSISTANT_ANALYSIS_FETCH: {
          const {onLoadEnd = null} = action.payload.extra;
          const {suggestions, response} = action.payload.response;

          const newHistory = state.history.filter(({status}) => status !== STATUS.pending);
          if (response) {
            newHistory.push({
              message: response,
              sender: SENDER.bot,
              status: STATUS.loading,
              hideAvatar: true,
              isAnalyzeMessage: true,
              analyzedPartially: action.payload.extra.analyzedPartially,
              analyzedDataCount: action.payload.extra.analyzedDataCount,
              onLoadEnd: onLoadEnd
            });
          }

          return {
            ...state,
            history: newHistory,
            suggestions: suggestions
          };
        }

        case AI_ASSISTANT_AI_SETTINGS_AUTHORIZATION_REQUEST:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              authToken: action.payload.response?.token ?? null
            }
          };
        case AI_ASSISTANT_NODES_FETCH:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              nodes: (action.payload?.response || []).sort((a, b) => a.order - b.order)
            }
          };
        case AI_ASSISTANT_AI_SETTINGS_FETCH:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              aiSettings: action.payload.response ?? INITIAL_AI_SETTINGS
            }
          };
        case AI_ASSISTANT_AI_SETTINGS_SUBMIT: {
          return {
            ...state,
            aiNodeSettings: {
              ...initialNodeAiSettingsState,
              authToken: state.aiNodeSettings.authToken
            }
          };
        }
        case AI_ASSISTANT_SEARCH_ENGINE_LIST_FETCH:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              searchEngines: action.payload.response
            }
          };
        case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_FETCH:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              searchEngineSettings: action.payload.response
            }
          };
        case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_SUBMIT:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              isAiSearchEngineSettingsVisible: false,
              searchEngineId: null,
              searchEngineSettings: null,
              searchEngines: null
            }
          };
        case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_DELETE:
          return {
            ...state,
            aiNodeSettings: {
              ...state.aiNodeSettings,
              searchEngines: null
            }
          };
        case AI_ASSISTANT_AI_FEATURES_FETCH:
          return {
            ...state,
            enabledFeatures: action.payload.response ?? {}
          };
        default:
          return state;
      }
    }

    case REQUEST_ERROR: {
      switch (action.payload.label) {
        case AI_ASSISTANT_ANALYSIS_FETCH:
        case AI_ASSISTANT_CHATBOT_REQUEST_SEND: {
          const newHistory = state.history.filter(({status}) => status !== STATUS.pending);
          newHistory.push({
            message: null,
            sender: SENDER.bot,
            status: STATUS.error,
            errorCode: !action.payload.statusCode
              ? ERROR_CODE.networkErr
              : action.payload.statusCode === 504
                ? ERROR_CODE.timeout
                : ERROR_CODE.generic
          });

          return {
            ...state,
            history: newHistory
          };
        }
        case AI_ASSISTANT_VIEW_SUBMIT: {
          return {
            ...state,
            isViewErrorVisible: !!(action.payload.statusCode === 409 && action.payload.response),
            viewErrorMessage: action.payload.response
          };
        }
        case AI_ASSISTANT_AI_SETTINGS_FETCH:
        case AI_ASSISTANT_AI_SETTINGS_SUBMIT:
        case AI_ASSISTANT_SEARCH_ENGINE_LIST_FETCH:
        case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_FETCH:
        case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_SUBMIT:
        case AI_ASSISTANT_SEARCH_ENGINE_SETTINGS_DELETE:
        case AI_ASSISTANT_AI_SETTINGS_CHANGE_PASSWORD_REQUEST:
          return {
            ...state,
            aiNodeSettings: {
              ...initialNodeAiSettingsState
            }
          };
        default:
          return state;
      }
    }

    default:
      return state;
  }
};

export default aiAssistantReducer;
